import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA, TextCTA } from "@/components/CTA";
import { TrustMarquee } from "@/components/TrustMarquee";
import { Tick } from "@/components/Annotation";
import { SectionLabel, Tape, StickyNote, PunchHoles } from "@/components/Notebook";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES, BRAND } from "@/config/links";
import { useLang } from "@/i18n";
import { useSectionTracking } from "@/hooks/useSectionTracking";

/* -------------------------------------------------------------------------
 *  Home page — all copy driven by the i18n dictionary (home.* keys)
 * -----------------------------------------------------------------------*/
export default function Home() {
  const { t, l } = useLang();
  const paths = t("home.paths");
  const channels = t("home.belief_channels");
  const faq = t("home.faq");

  const pathHrefs = [
    ROUTES.highStakesPresentationCoaching,
    ROUTES.executivePresenceDevelopment,
    ROUTES.corporateTraining,
    ROUTES.ldPartnerships,
  ];

  const pathwaysRef = useSectionTracking("home-pathways");
  const mapRef = useSectionTracking("home-map");
  const beliefRef = useSectionTracking("home-belief");
  const aboutTeaserRef = useSectionTracking("home-about-teaser");
  const testimonialsRef = useSectionTracking("home-testimonials-excerpt");
  const faqRef = useSectionTracking("home-faq");
  const finalCtaRef = useSectionTracking("home-final-cta");

  return (
    <>
      <PageMeta titleKey="meta.home_title" descriptionKey="meta.home_description" path="/" />
      <PageHero
        testId="home-hero"
        eyebrow={t("home.hero_eyebrow")}
        headline={[t("home.hero_h1_a"), t("home.hero_h1_b")]}
        emphasis={t("home.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("home.hero_intro")}
        meta={
          <>
            {t("home.hero_meta_1")} · <br />
            {t("home.hero_meta_2")} · <br />
            {t("home.hero_meta_3")}
          </>
        }
      >
        <PrimaryCTA href={ROUTES.booking} testId="home-book-cta" location="hero" targetService="general_booking">
          {t("cta.book_consultation")}
        </PrimaryCTA>
        <OutlineCTA
          href={ROUTES.nextPresentationReadinessCheck}
          testId="home-audit-cta"
          location="hero"
          targetAssetId="next_presentation_readiness"
        >
          {t("landing.hero_diagnostic_cta")}
        </OutlineCTA>
      </PageHero>

      <TrustMarquee />

      {/* 4-PERSONA CHOOSER */}
      <section
        id="pathways"
        ref={pathwaysRef}
        className="relative z-10 py-24 md:py-36 overflow-hidden"
        data-testid="home-pathways"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 md:mb-20 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("home.paths_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {t("home.paths_headline_a")}
              <span className="italic">{t("home.paths_headline_emph")}</span>
            </h2>
          </Reveal>

          <StaggerGroup className="grid md:grid-cols-2 gap-6 md:gap-8">
            {Array.isArray(paths) &&
              paths.map((p, i) => (
                <StaggerItem key={i}>
                  <Link
                    to={l(pathHrefs[i])}
                    data-testid={`home-path-0${i + 1}`}
                    className="group relative block bg-paper border border-ink/12 shadow-[3px_6px_28px_rgba(27,27,24,0.08)] p-7 md:p-9 hover:border-terracotta transition-colors h-full"
                  >
                    <span className="absolute top-6 right-6 font-serif text-4xl md:text-5xl text-terracotta/60 leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="font-hand text-terracotta text-lg md:text-xl leading-snug pr-14 max-w-md">
                      &ldquo;{p.mindset}&rdquo;
                    </p>
                    <p className="font-body text-xs uppercase tracking-[0.22em] text-ink-soft mt-6">
                      {p.who}
                    </p>
                    <h3 className="font-serif text-2xl md:text-3xl text-ink mt-3 group-hover:text-terracotta transition-colors">
                      {p.lead}
                    </h3>
                    <p className="font-body text-ink-soft mt-3 leading-relaxed">{p.body}</p>
                    <p className="font-hand text-mustard text-lg mt-5">{p.duration}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.22em] text-ink group-hover:text-terracotta transition-colors">
                      {t("home.paths_explore")}
                      <span className="transition-transform duration-300 group-hover:translate-x-1">
                        &rarr;
                      </span>
                    </span>
                  </Link>
                </StaggerItem>
              ))}
          </StaggerGroup>
        </div>
      </section>

      {/* FULL MAP — the practice at a glance */}
      <section className="relative z-10 py-20 md:py-28 overflow-hidden" ref={mapRef} data-testid="home-map">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal className="mb-10 md:mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("home.map_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {t("home.map_headline_a")} <span className="italic">{t("home.map_headline_b")}</span>
            </h2>
            <p className="mt-5 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              {t("home.map_intro")}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative">
              <Tape className="absolute -top-4 left-10 z-10" rotate={-5} width={130} />
              <Tape className="absolute -top-4 right-14 z-10" rotate={4} width={110} />
              <div className="bg-paper border border-ink/12 shadow-[8px_16px_56px_rgba(27,27,24,0.12)] p-3 md:p-5">
                <img
                  src="/sketches/home-mindmap.webp"
                  loading="lazy"
                  alt={t("home.map_alt")}
                  className="w-full h-auto block"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                  data-testid="home-map-image"
                />
              </div>
              <p className="mt-4 font-hand text-terracotta text-xl md:text-2xl text-center">
                {t("home.map_caption")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BELIEF — communication as an integrated system */}
      <section
        className="relative z-10 py-24 md:py-36 bg-paper-deep/40 overflow-hidden"
        ref={beliefRef}
        data-testid="home-belief"
      >
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal className="mb-10 md:mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("home.belief_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("home.belief_headline_a")}
              <span className="italic">{t("home.belief_headline_emph")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              {t("home.belief_body")}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative">
              <Tape className="absolute -top-4 left-1/4 z-10" rotate={-4} width={130} />
              <div className="bg-paper border border-ink/12 shadow-[8px_16px_56px_rgba(27,27,24,0.12)] p-3 md:p-5">
                <img
                  src="/sketches/home-journey.webp"
                  loading="lazy"
                  alt={t("home.belief_alt")}
                  className="w-full h-auto block"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                  data-testid="home-journey-image"
                />
              </div>
              <p className="mt-4 font-hand text-terracotta text-xl md:text-2xl text-center">
                {t("home.belief_caption")}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-10 md:mt-14 max-w-3xl mx-auto">
            <div className="relative bg-paper border border-ink/12 shadow-[3px_6px_28px_rgba(27,27,24,0.08)] p-6 md:p-8 md:pl-16">
              <PunchHoles className="hidden md:flex absolute left-5 top-8 bottom-8" count={7} />
              <SectionLabel className="mb-4 block">{t("home.belief_channels_label")}</SectionLabel>
              <ul className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4 md:gap-x-8">
                {Array.isArray(channels) &&
                  channels.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <Tick className="w-4 h-4 mt-1 shrink-0" />
                      <span className="font-body text-ink capitalize">{s}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="relative z-10 py-24 md:py-36" ref={aboutTeaserRef} data-testid="home-about-teaser">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <Tape className="absolute -top-4 left-8" rotate={-6} width={110} />
              <Tape className="absolute -top-4 right-8" rotate={5} width={110} />
              <div className="aspect-[4/5] bg-paper-deep/40 border border-ink/10 overflow-hidden">
                <img
                  src="/sketches/coach-portrait.webp"
                  srcSet="/sketches/coach-portrait-500.webp 500w, /sketches/coach-portrait.webp 900w"
                  sizes="(min-width: 1024px) 40vw, 90vw"
                  loading="lazy"
                  alt={t("about.portrait_alt", { name: BRAND.coach })}
                  className="w-full h-full object-cover object-[center_top]"
                  style={{ mixBlendMode: "multiply", filter: "contrast(1.05)" }}
                  draggable={false}
                  data-testid="home-coach-portrait"
                />
              </div>
              <p className="mt-3 font-hand text-terracotta text-xl">
                {t("about.portrait_caption", { name: BRAND.coach.split(" ")[0] })}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("home.about_teaser_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("home.about_teaser_headline_a")}
              <br />
              <span className="italic">{t("home.about_teaser_headline_b")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[54ch]">
              {t("home.about_teaser_body_1")}
            </p>
            <p className="mt-5 font-body text-lg text-ink-soft leading-relaxed max-w-[54ch]">
              {t("home.about_teaser_body_2")}
            </p>
            <div className="mt-8">
              <TextCTA href={ROUTES.about} testId="home-about-cta">
                {t("home.about_teaser_cta")}
              </TextCTA>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIAL EXCERPT */}
      <section
        className="relative z-10 py-24 md:py-32 border-y border-ink/10"
        ref={testimonialsRef}
        data-testid="home-testimonials-excerpt"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal className="text-center">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("home.testimonial_eyebrow")}
            </p>
            <blockquote className="font-serif italic text-3xl md:text-4xl lg:text-5xl text-ink leading-snug max-w-3xl mx-auto">
              &ldquo;{t("home.testimonial_quote")}&rdquo;
            </blockquote>
            <p className="mt-8 font-body text-ink-soft">{t("home.testimonial_author")}</p>
            <div className="mt-10">
              <TextCTA
                href={ROUTES.highStakesPresentationCoaching + "#testimonials"}
                testId="home-testimonials-more"
              >
                {t("home.testimonial_more")}
              </TextCTA>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ EXCERPT */}
      <section className="relative z-10 py-24 md:py-36 bg-paper-deep/40" ref={faqRef} data-testid="home-faq">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("home.faq_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("home.faq_h_a")}
              <span className="italic">{t("home.faq_h_emph")}</span>
            </h2>
          </Reveal>
          <div className="border-t border-ink/15">
            {Array.isArray(faq) &&
              faq.map((f, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <details className="group border-b border-ink/15">
                    <summary
                      className="list-none py-6 flex items-start justify-between gap-6 cursor-pointer font-serif text-xl md:text-2xl text-ink hover:text-terracotta transition-colors"
                      data-testid={`home-faq-${i}`}
                    >
                      <span>{f.q}</span>
                      <span className="font-hand text-terracotta text-2xl leading-none shrink-0 mt-1 transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <div className="pb-6 pr-8">
                      <p className="font-body text-base md:text-lg text-ink-soft leading-relaxed max-w-2xl">
                        {f.a}
                      </p>
                    </div>
                  </details>
                </Reveal>
              ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="relative z-10 py-24 md:py-32 bg-ink text-paper overflow-hidden"
        ref={finalCtaRef}
        data-testid="home-final-cta"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-6">
              {t("home.final_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-paper">
              {t("home.final_h_a")}
              <br />
              <span className="italic">{t("home.final_h_b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-10">
            <StickyNote className="inline-block" rotate={-3} color="#F2EBDA">
              {t("home.final_note")}
            </StickyNote>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-5">
              <PrimaryCTA href={ROUTES.booking} testId="home-final-book-cta" location="final_cta" targetService="general_booking">
                {t("cta.book_consultation")}
              </PrimaryCTA>
              <OutlineCTA
                href={ROUTES.contact}
                testId="home-final-contact-cta"
                location="final_cta"
                targetAssetId="contact_request"
                className="!border-paper/75 !text-paper hover:!border-mustard hover:!text-mustard"
              >
                {t("cta.send_message")}
              </OutlineCTA>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
